import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Handlebars from "handlebars";
import dayjs from "dayjs";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import {
  getBookingTimes,
  checkDayAvailability,
  getBookingTimesV2,
} from "../utils/api";
import { AppDispatch } from "../redux/store";
import { AppointmentState, ShopSchedule } from "../types";
import {
  StayOrDropSelector,
  DatePicker,
  TimePicker,
  RideOptionPicker,
  AppointmentSummary,
  NextBackButtons,
} from "components";
import { ProspectiveCalendarAppointment } from "types/calendar-appointment.interface";
import { AvailableSlotsInterfaceV2 } from "types/available-slots.interface";
import { ApptType } from "types/booking-config.interface";

interface AppointmentPickerProps {
  appointment: AppointmentState;
}

interface DayAvailability {
  [date: string]: boolean;
}

const AppointmentPickerV2: React.FC<AppointmentPickerProps> = ({
  appointment,
}) => {
  const sameDayBookings: boolean =
    appointment?.shopData?.sameDayBookings !== undefined
      ? appointment?.shopData?.sameDayBookings
      : false;
  const defaultSchedule: ShopSchedule[] =
    appointment?.shopData?.shopSchedule || [];

  const [stayOrDropOff, setStayOrDropOff] = useState<ApptType | undefined>(
    appointment?.slot?.dropOff
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(
    appointment?.slot?.date ? appointment.slot.date : null
  );
  const [selectedTime, setSelectedTime] = useState<string>(
    appointment?.slot?.time || ""
  );
  const [complimentaryRide, setComplimentaryRide] = useState<string>("");

  const [noSlotsMessage, setNoSlotsMessage] = useState<string>(
    "Unfortunately, there are no available time slots at the moment. Please try again later or contact us directly."
  );
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [bounceComplimentaryRide, setBounceComplimentaryRide] =
    useState<boolean>(false);
  const [bookingTimes, setBookingTimes] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [clear, setClear] = useState<boolean>(false);
  const [findingAvailableDay, setFindingAvailableDay] =
    useState<boolean>(false);
  const [dayAvailability, setDayAvailability] = useState<DayAvailability>({});

  const dispatch = useDispatch<AppDispatch>();

  ///////////
  const [availabilityResponse, setAvailabilityResponse] = useState<
    AvailableSlotsInterfaceV2 | undefined
  >(undefined);

  //build handelbars
  useEffect(() => {
    const template = Handlebars.compile(
      appointment?.shopData?.messages?.noSlots ??
        "Unfortunately, there are no available time slots at the moment. Please try again later or contact us directly."
    );

    const data = {
      your_service: appointment?.service,
      on_date: appointment?.date,
      at_time: appointment?.time,
    };
    const result = template(data);

    setStartDate(
      appointment.shopData?.sameDayBookings
        ? new Date()
        : dayjs().add(1, "day").toDate()
    );
    setNoSlotsMessage(result);
  }, []);

  // Find the first available day when the appointment type is selected
  useEffect(() => {
    if (stayOrDropOff) {
      fetchSlotTimes(startDate);
    }
  }, [stayOrDropOff, startDate]);

  const fetchSlotTimes = async (startDate: Date): Promise<void> => {
    setLoading(true);
    const prespectiveAppointment: ProspectiveCalendarAppointment = {
      ...{ ...appointment, shopData: undefined },
      apptType: stayOrDropOff,
      startDateRange: startDate,
      endDateRange: dayjs(startDate).add(6, "day").toDate(),
    };
    const result: AvailableSlotsInterfaceV2 = await getBookingTimesV2(
      prespectiveAppointment
    );

    setAvailabilityResponse(result);
    setLoading(false);
  };

  return (
    <>
      {/* <textarea>{JSON.stringify(appointment, null, 2)}</textarea> */}
      <div className="mx-6 pt-6 ">
        <div className="mb-4">
          <StayOrDropSelector
            appointment={appointment}
            stayOrDropOff={stayOrDropOff}
            setStayOrDropOff={setStayOrDropOff}
          />
        </div>

        {stayOrDropOff && (
          <div className="mb-4">
            <DatePicker
              appointment={appointment}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              startDate={startDate}
              setStartDate={setStartDate}
              availabilityResponse={availabilityResponse}
              dayAvailability={dayAvailability}
              findingAvailableDay={findingAvailableDay}
              loadingDates={loading}
            />
          </div>
        )}

        {selectedDate && (
          <div className="mb-4">
            <TimePicker
              appointment={appointment}
              selectedTime={selectedTime}
              setSelectedTime={setSelectedTime}
              bookingTimes={
                (stayOrDropOff === "wait"
                  ? availabilityResponse?.dates[selectedDate]?.waiterSlots
                  : availabilityResponse?.dates[selectedDate]?.dropOffSlots) ??
                []
              }
              loading={loading}
              noSlotsMessage={noSlotsMessage}
              setClear={setClear}
              setBounceComplimentaryRide={setBounceComplimentaryRide}
            />
          </div>
        )}

        {selectedTime && appointment?.shopData?.complimentaryRide && (
          <div className="mb-4">
            <RideOptionPicker
              appointment={appointment}
              complimentaryRide={complimentaryRide}
              setComplimentaryRide={setComplimentaryRide}
              bounceComplimentaryRide={bounceComplimentaryRide}
              setBounceComplimentaryRide={setBounceComplimentaryRide}
              setClear={setClear}
            />
          </div>
        )}

        {selectedTime &&
          stayOrDropOff &&
          (complimentaryRide || !appointment?.shopData?.complimentaryRide) && (
            <div className="mb-4">
              <AppointmentSummary
                appointment={appointment}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                stayOrDropOff={stayOrDropOff}
                setClear={setClear}
              />
            </div>
          )}
      </div>

      <div>
        <NextBackButtons appointment={appointment} clear={clear} />
      </div>
    </>
  );
};

export { AppointmentPickerV2 };
